import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import api from '../lib/api';
import { TaskDto, TaskStatus, TaskType, TaskFilterDto, PaginatedProcessTasksDto } from '@prometeus/contracts';
import { TaskFormModal } from '../components/TaskFormModal';
import { TaskDetailModal } from '../components/TaskDetailModal';
import { ArchivedProcessBlockModal } from '../components/ArchivedProcessBlockModal';
import { usePermissionGuard } from '../hooks/usePermissionGuard';

export const Route = createFileRoute('/w/$workspaceId/clients/$clientId/processes/$processId/tasks')({
  validateSearch: (search: Record<string, unknown>) => ({
    taskId: (search.taskId as string) || undefined,
  }),
  component: ProcessTasksTab,
});

const LIST_PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 1000;

// Component for assignee avatar in task list
function TaskAssigneeAvatar({ assignee }: { assignee: { userName: string; avatarUrl?: string | null } }) {
  const [showTooltip, setShowTooltip] = useState(false);
  const [avatarError, setAvatarError] = useState(false);

  const getInitials = (name: string | null | undefined): string => {
    if (!name || typeof name !== 'string' || name.length === 0) return '?';
    const words = name.trim().split(/\s+/);
    if (words.length >= 2) {
      return (words[0][0] + words[words.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <div
      className="relative"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {assignee.avatarUrl && assignee.avatarUrl.trim() !== '' && !avatarError ? (
        <>
          <img
            src={assignee.avatarUrl}
            alt={assignee.userName}
            className="w-8 h-8 rounded-full object-cover transition-opacity hover:opacity-90"
            onError={() => setAvatarError(true)}
          />
          {showTooltip && (
            <div
              className="absolute left-0 top-10 z-50 px-2.5 py-1.5 whitespace-nowrap pointer-events-none rounded-[10px]"
              style={{
                fontSize: 11,
                color: '#F5F6F5',
                backgroundColor: '#06141B',
                boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
              }}
            >
              {assignee.userName}
            </div>
          )}
        </>
      ) : (
        <>
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium"
            style={{ backgroundColor: '#06141B', color: '#F5F6F5', fontSize: 12 }}
          >
            {getInitials(assignee.userName)}
          </div>
          {showTooltip && (
            <div
              className="absolute left-0 top-10 z-50 px-2.5 py-1.5 whitespace-nowrap pointer-events-none rounded-[10px]"
              style={{
                fontSize: 11,
                color: '#F5F6F5',
                backgroundColor: '#06141B',
                boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
              }}
            >
              {assignee.userName}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ProcessTasksTab() {
  const { workspaceId, clientId, processId } = Route.useParams();
  const { taskId: searchTaskId } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [showArchivedBlockModal, setShowArchivedBlockModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [editingTask, setEditingTask] = useState<TaskDto | null>(null);
  const [selectedTask, setSelectedTask] = useState<TaskDto | null>(null);
  const [statusFilter, setStatusFilter] = useState<TaskStatus[]>([]);
  const [typeFilter, setTypeFilter] = useState<TaskType[]>([]);
  const [assigneeFilter, setAssigneeFilter] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [listPage, setListPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [menuOpenTaskId, setMenuOpenTaskId] = useState<string | null>(null);
  const [taskMenuPosition, setTaskMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const [filterDropdownOpen, setFilterDropdownOpen] = useState<'status' | 'type' | 'assignee' | null>(null);
  const menuRef = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const menuTriggerRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});
  const TASK_MENU_WIDTH = 192; // w-48
  const { requirePermission } = usePermissionGuard();
  const filterDropdownRef = useRef<HTMLDivElement>(null);

  const filters: TaskFilterDto = useMemo(
    () => ({
      status: statusFilter.length > 0 ? statusFilter : undefined,
      type: typeFilter.length > 0 ? typeFilter : undefined,
      assignee: assigneeFilter || undefined,
    }),
    [statusFilter, typeFilter, assigneeFilter],
  );

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setListPage(1);
  }, [debouncedSearch, statusFilter, typeFilter, assigneeFilter, sortOrder]);

  const toggleStatusFilter = (value: TaskStatus) => {
    setStatusFilter((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  const toggleTypeFilter = (value: TaskType) => {
    setTypeFilter((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  const { data: members } = useQuery({
    queryKey: ['members', workspaceId],
    queryFn: async () => {
      const response = await api.get('/members');
      return response.data.data;
    },
  });

  const { data: process } = useQuery({
    queryKey: ['processes', workspaceId, clientId, processId],
    queryFn: async () => {
      const response = await api.get(`/clients/${clientId}/processes/${processId}`);
      return response.data.data;
    },
    enabled: !!processId && !!clientId,
  });

  const { data: listPageData, isLoading, isFetching } = useQuery<PaginatedProcessTasksDto>({
    queryKey: [
      'tasks',
      workspaceId,
      processId,
      'list',
      listPage,
      debouncedSearch,
      statusFilter,
      typeFilter,
      assigneeFilter,
      sortOrder,
    ],
    queryFn: async () => {
      const response = await api.get(`/processes/${processId}/tasks`, {
        params: {
          page: listPage,
          limit: LIST_PAGE_SIZE,
          dateSort: sortOrder === 'asc' ? 'asc' : 'desc',
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
          ...filters,
        },
      });
      const raw = response.data.data;
      if (raw && typeof raw === 'object' && Array.isArray((raw as PaginatedProcessTasksDto).items)) {
        return raw as PaginatedProcessTasksDto;
      }
      throw new Error('Resposta de tarefas inválida');
    },
    enabled: !!processId,
    placeholderData: (previousData) => previousData,
  });

  const tasks = listPageData?.items ?? [];
  const totalListCount = listPageData?.total ?? 0;
  const totalListPages = Math.max(1, Math.ceil(totalListCount / LIST_PAGE_SIZE));

  useEffect(() => {
    if (listPageData == null) return;
    if (listPage > totalListPages) {
      setListPage(totalListPages);
    }
  }, [listPage, totalListPages, listPageData]);

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/tasks/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', workspaceId, processId], exact: false });
    },
  });

  // Posição do menu (portal em document.body — evita corte por overflow no card/tabela)
  useEffect(() => {
    if (!menuOpenTaskId) {
      setTaskMenuPosition(null);
      return;
    }
    const btn = menuTriggerRefs.current[menuOpenTaskId];
    if (!btn) {
      setTaskMenuPosition(null);
      return;
    }
    const rect = btn.getBoundingClientRect();
    setTaskMenuPosition({
      top: rect.bottom + 4,
      left: rect.right - TASK_MENU_WIDTH,
    });
  }, [menuOpenTaskId]);

  // Fechar menu ao clicar fora (dropdown está fora do ref via portal)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!menuOpenTaskId) return;
      const target = event.target as HTMLElement;
      const wrap = menuRef.current[menuOpenTaskId];
      if (
        wrap &&
        !wrap.contains(target) &&
        !target.closest('[data-task-row-dropdown]')
      ) {
        setMenuOpenTaskId(null);
      }
    };
    if (menuOpenTaskId) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpenTaskId]);

  // Fechar ao rolar (dropdown fixo ficaria desalinhado)
  useEffect(() => {
    if (!menuOpenTaskId) return;
    const handleScroll = () => setMenuOpenTaskId(null);
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [menuOpenTaskId]);

  // Fechar dropdown de filtro ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target as Node)) {
        setFilterDropdownOpen(null);
      }
    };
    if (filterDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [filterDropdownOpen]);

  // Abrir modal da tarefa quando taskId vem na URL (lista é paginada no backend)
  useEffect(() => {
    if (!searchTaskId) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await api.get(`/tasks/${searchTaskId}`);
        const task = response.data.data as TaskDto;
        if (cancelled || task.processId !== processId) return;
        setSelectedTask(task);
        setShowDetailModal(true);
        navigate({ to: '.', search: { taskId: undefined } } as { to: '.'; search: { taskId?: string } });
      } catch {
        /* tarefa inexistente ou sem acesso */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [searchTaskId, processId, navigate]);

  const formatDate = (date: Date | string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatDateTime = (date: Date | string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTaskStatusLabel = (status: TaskStatus) => {
    switch (status) {
      case 'BACKLOG':
        return 'Aguardando';
      case 'IN_PROGRESS':
        return 'Em Progresso';
      case 'BLOCKED':
        return 'Bloqueada';
      case 'ON_HOLD':
        return 'Suspensa';
      case 'DONE':
        return 'Concluída';
      case 'CANCELED':
        return 'Cancelada';
      default:
        return status;
    }
  };

  const getTaskStatusColor = (status: TaskStatus) => {
    switch (status) {
      case 'DONE':
        return '#10B981';
      case 'IN_PROGRESS':
        return '#3182CE';
      case 'BLOCKED':
        return '#E53E3E';
      case 'ON_HOLD':
        return '#D69E2E';
      case 'BACKLOG':
        return '#718096';
      case 'CANCELED':
        return '#9CA3AF';
      default:
        return '#718096';
    }
  };

  const getTaskTypeLabel = (type: TaskType) => {
    switch (type) {
      case 'DEADLINE':
        return 'Prazo';
      case 'EVENT':
        return 'Evento';
      case 'TASK':
        return 'Tarefa';
      case 'HEARING':
        return 'Audiência';
      default:
        return type;
    }
  };

  const getTaskTypeColor = (type: TaskType) => {
    switch (type) {
      case 'DEADLINE':
        return '#E53E3E'; // Vermelho
      case 'EVENT':
        return '#10B981'; // Verde
      case 'TASK':
        return '#3182CE'; // Azul
      case 'HEARING':
        return '#D69E2E'; // Amarelo
      default:
        return '#718096';
    }
  };

  const truncateText = (text: string | null | undefined, maxLength: number) => {
    if (!text) return '-';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const handleDeleteTask = (taskId: string) => {
    if (process?.archivedAt) {
      setShowArchivedBlockModal(true);
      setMenuOpenTaskId(null);
      return;
    }
    if (!requirePermission('tasks.delete', 'Excluir tarefas')) {
      setMenuOpenTaskId(null);
      return;
    }
    if (confirm('Tem certeza que deseja excluir esta tarefa?')) {
      deleteMutation.mutate(taskId);
      setMenuOpenTaskId(null);
    }
  };

  const handleEditTask = (task: TaskDto) => {
    if (process?.archivedAt) {
      setShowArchivedBlockModal(true);
      setMenuOpenTaskId(null);
      return;
    }
    if (!requirePermission('tasks.update', 'Editar tarefas')) {
      setMenuOpenTaskId(null);
      return;
    }
    setEditingTask(task);
    setShowModal(true);
    setMenuOpenTaskId(null);
  };

  const openMenuTask = menuOpenTaskId
    ? tasks.find((t) => t.id === menuOpenTaskId) ?? null
    : null;

  const searchPending = searchInput.trim() !== debouncedSearch;
  const showSearchLoading = searchPending || (isFetching && !isLoading);

  if (isLoading) {
    return (
      <div className="pt-4 pb-8">
        <div className="rounded-[15px] p-6 animate-pulse" style={{ backgroundColor: '#F9FAFB', boxShadow: '0 0 15px rgba(0, 0, 0, 0.06)' }}>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 rounded-[10px]" style={{ backgroundColor: 'rgba(6, 20, 27, 0.06)' }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-4 pb-8">
      <div
        className="bg-white rounded-[15px] flex flex-col overflow-hidden"
        style={{ fontFamily: 'Poppins, sans-serif', boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)' }}
      >
        <div className="px-6 pt-4 pb-2" style={{ borderBottom: '1px solid rgba(6, 20, 27, 0.08)' }}>
          <div className="flex justify-between items-center">
            <div>
              <h2 className="font-medium" style={{ fontSize: 16, color: '#06141B' }}>Tarefas</h2>
              <p className="text-[12px] mt-0.5" style={{ color: '#4A5C6A' }}>
                {totalListCount} {totalListCount === 1 ? 'tarefa' : 'tarefas'}
              </p>
            </div>
            <button
              onClick={() => {
                if (process?.archivedAt) {
                  setShowArchivedBlockModal(true);
                  return;
                }
                if (!requirePermission('tasks.create', 'Criar tarefas')) return;
                setEditingTask(null);
                setShowModal(true);
              }}
              className="w-8 h-8 flex items-center justify-center rounded-[10px] transition-opacity hover:opacity-90"
              style={{ backgroundColor: '#06141B', color: '#F5F6F5', fontSize: 18 }}
              title="Nova Tarefa"
            >
              +
            </button>
          </div>

          {/* Busca (debounce 1s + loading) */}
          <div className="mt-4 relative">
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Buscar por título..."
              className="w-full px-3 py-2 rounded-[10px] pr-10"
              style={{
                fontSize: 12,
                fontWeight: 400,
                backgroundColor: '#F9FAFB',
                color: '#2D3748',
                border: '1px solid rgba(6, 20, 27, 0.08)',
              }}
              aria-busy={showSearchLoading}
            />
            {showSearchLoading && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" aria-hidden>
                <svg className="animate-spin h-4 w-4 text-[#4A5C6A]" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </div>
            )}
          </div>

          {/* Filtros — dropdowns no padrão (branco, sombra, divisores, espaçamento) */}
          <div ref={filterDropdownRef} className="mt-4 flex items-center gap-3 flex-wrap">
            {/* Status */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setFilterDropdownOpen((o) => (o === 'status' ? null : 'status'))}
                className="inline-flex items-center justify-between gap-2 px-3 py-2 rounded-[10px] transition-opacity hover:opacity-90 min-w-[160px]"
                style={{
                  fontSize: 12,
                  fontWeight: 400,
                  backgroundColor: '#F9FAFB',
                  color: '#2D3748',
                  border: '1px solid rgba(6, 20, 27, 0.08)',
                }}
              >
                <span>
                  {statusFilter.length === 0
                    ? 'Todos os Status'
                    : statusFilter.length === 1
                      ? getTaskStatusLabel(statusFilter[0])
                      : `${statusFilter.length} status selecionados`}
                </span>
                <svg className="w-4 h-4 flex-shrink-0 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {filterDropdownOpen === 'status' && (
                <div
                  className="absolute left-0 top-full mt-1 z-50 min-w-[180px] py-2 rounded-[10px]"
                  style={{
                    backgroundColor: '#fff',
                    boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
                    border: '1px solid rgba(6, 20, 27, 0.08)',
                  }}
                >
                  {[
                    { value: '', label: 'Todos os Status' },
                    { value: 'BACKLOG', label: 'Aguardando' },
                    { value: 'IN_PROGRESS', label: 'Em Progresso' },
                    { value: 'BLOCKED', label: 'Bloqueada' },
                    { value: 'ON_HOLD', label: 'Suspensa' },
                    { value: 'DONE', label: 'Concluída' },
                    { value: 'CANCELED', label: 'Cancelada' },
                  ].map((opt) => (
                    <button
                      key={opt.value || 'all'}
                      type="button"
                      onClick={() => {
                        if (!opt.value) {
                          setStatusFilter([]);
                        } else {
                          toggleStatusFilter(opt.value as TaskStatus);
                        }
                      }}
                      className="w-full text-left px-4 py-2.5 transition-colors border-b border-[rgba(6,20,27,0.06)] last:border-b-0 hover:bg-[#F9FAFB] first:pt-0 last:pb-0"
                      style={{
                        fontSize: 12,
                        fontWeight: 400,
                        color:
                          (!opt.value && statusFilter.length === 0) ||
                          (opt.value && statusFilter.includes(opt.value as TaskStatus))
                            ? '#06141B'
                            : '#2D3748',
                      }}
                    >
                      <span
                        className="inline-flex items-center justify-center mr-2 h-3.5 w-3.5 rounded-[4px] border"
                        style={{
                          borderColor:
                            (!opt.value && statusFilter.length === 0) ||
                            (opt.value && statusFilter.includes(opt.value as TaskStatus))
                              ? '#06141B'
                              : 'rgba(6, 20, 27, 0.3)',
                          backgroundColor:
                            (!opt.value && statusFilter.length === 0) ||
                            (opt.value && statusFilter.includes(opt.value as TaskStatus))
                              ? '#06141B'
                              : '#FFFFFF',
                        }}
                      >
                        {((!opt.value && statusFilter.length === 0) ||
                          (opt.value && statusFilter.includes(opt.value as TaskStatus))) && (
                          <svg
                            width="8"
                            height="8"
                            viewBox="0 0 12 10"
                            fill="none"
                          >
                            <path
                              d="M1 5L4 8L11 1"
                              stroke="#F5F6F5"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </span>
                      <span>{opt.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Tipo */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setFilterDropdownOpen((o) => (o === 'type' ? null : 'type'))}
                className="inline-flex items-center justify-between gap-2 px-3 py-2 rounded-[10px] transition-opacity hover:opacity-90 min-w-[140px]"
                style={{
                  fontSize: 12,
                  fontWeight: 400,
                  backgroundColor: '#F9FAFB',
                  color: '#2D3748',
                  border: '1px solid rgba(6, 20, 27, 0.08)',
                }}
              >
                <span>
                  {typeFilter.length === 0
                    ? 'Todos os Tipos'
                    : typeFilter.length === 1
                      ? getTaskTypeLabel(typeFilter[0])
                      : `${typeFilter.length} tipos selecionados`}
                </span>
                <svg className="w-4 h-4 flex-shrink-0 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {filterDropdownOpen === 'type' && (
                <div
                  className="absolute left-0 top-full mt-1 z-50 min-w-[160px] py-2 rounded-[10px]"
                  style={{
                    backgroundColor: '#fff',
                    boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
                    border: '1px solid rgba(6, 20, 27, 0.08)',
                  }}
                >
                  {[
                    { value: '', label: 'Todos os Tipos' },
                    { value: 'TASK', label: 'Tarefa' },
                    { value: 'DEADLINE', label: 'Prazo' },
                    { value: 'EVENT', label: 'Evento' },
                    { value: 'HEARING', label: 'Audiência' },
                  ].map((opt) => (
                    <button
                      key={opt.value || 'all'}
                      type="button"
                      onClick={() => {
                        if (!opt.value) {
                          setTypeFilter([]);
                        } else {
                          toggleTypeFilter(opt.value as TaskType);
                        }
                      }}
                      className="w-full text-left px-4 py-2.5 transition-colors border-b border-[rgba(6,20,27,0.06)] last:border-b-0 hover:bg-[#F9FAFB] first:pt-0 last:pb-0"
                      style={{
                        fontSize: 12,
                        fontWeight: 400,
                        color:
                          (!opt.value && typeFilter.length === 0) ||
                          (opt.value && typeFilter.includes(opt.value as TaskType))
                            ? '#06141B'
                            : '#2D3748',
                      }}
                    >
                      <span
                        className="inline-flex items-center justify-center mr-2 h-3.5 w-3.5 rounded-[4px] border"
                        style={{
                          borderColor:
                            (!opt.value && typeFilter.length === 0) ||
                            (opt.value && typeFilter.includes(opt.value as TaskType))
                              ? '#06141B'
                              : 'rgba(6, 20, 27, 0.3)',
                          backgroundColor:
                            (!opt.value && typeFilter.length === 0) ||
                            (opt.value && typeFilter.includes(opt.value as TaskType))
                              ? '#06141B'
                              : '#FFFFFF',
                        }}
                      >
                        {((!opt.value && typeFilter.length === 0) ||
                          (opt.value && typeFilter.includes(opt.value as TaskType))) && (
                          <svg
                            width="8"
                            height="8"
                            viewBox="0 0 12 10"
                            fill="none"
                          >
                            <path
                              d="M1 5L4 8L11 1"
                              stroke="#F5F6F5"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </span>
                      <span>{opt.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Responsável */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setFilterDropdownOpen((o) => (o === 'assignee' ? null : 'assignee'))}
                className="inline-flex items-center justify-between gap-2 px-3 py-2 rounded-[10px] transition-opacity hover:opacity-90 min-w-[180px]"
                style={{
                  fontSize: 12,
                  fontWeight: 400,
                  backgroundColor: '#F9FAFB',
                  color: '#2D3748',
                  border: '1px solid rgba(6, 20, 27, 0.08)',
                }}
              >
                <span>
                  {assigneeFilter
                    ? (members?.find((m: any) => m.id === assigneeFilter)?.user?.name || members?.find((m: any) => m.id === assigneeFilter)?.user?.email || 'N/A')
                    : 'Todos os Responsáveis'}
                </span>
                <svg className="w-4 h-4 flex-shrink-0 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {filterDropdownOpen === 'assignee' && (
                <div
                  className="absolute left-0 top-full mt-1 z-50 min-w-[200px] max-h-[280px] overflow-y-auto py-2 rounded-[10px]"
                  style={{
                    backgroundColor: '#fff',
                    boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
                    border: '1px solid rgba(6, 20, 27, 0.08)',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setAssigneeFilter('');
                      setFilterDropdownOpen(null);
                    }}
                    className="w-full text-left px-4 py-2.5 transition-colors border-b border-[rgba(6,20,27,0.06)] hover:bg-[#F9FAFB]"
                    style={{
                      fontSize: 12,
                      fontWeight: 400,
                      color: !assigneeFilter ? '#06141B' : '#2D3748',
                    }}
                  >
                    Todos os Responsáveis
                  </button>
                  {members?.map((member: any) => (
                    <button
                      key={member.id}
                      type="button"
                      onClick={() => {
                        setAssigneeFilter(member.id);
                        setFilterDropdownOpen(null);
                      }}
                      className="w-full text-left px-4 py-2.5 transition-colors border-b border-[rgba(6,20,27,0.06)] last:border-b-0 hover:bg-[#F9FAFB]"
                      style={{
                        fontSize: 12,
                        fontWeight: 400,
                        color: assigneeFilter === member.id ? '#06141B' : '#2D3748',
                      }}
                    >
                      {member.user?.name || member.user?.email || 'N/A'}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="px-3 py-2 rounded-[10px] transition-opacity hover:opacity-90 flex items-center gap-2"
              style={{
                fontSize: 12,
                fontWeight: 400,
                backgroundColor: '#F9FAFB',
                color: '#2D3748',
                border: '1px solid rgba(6, 20, 27, 0.08)',
              }}
              title={sortOrder === 'asc' ? 'Mais antigas primeiro' : 'Mais recentes primeiro'}
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ transform: sortOrder === 'desc' ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {sortOrder === 'asc' ? 'Mais Antigas' : 'Mais Recentes'}
            </button>
          </div>
        </div>

        <div className="p-6">
          {tasks.length > 0 ? (
            <div className={`overflow-x-auto transition-opacity ${isFetching && !isLoading ? 'opacity-70' : ''}`}>
              <table className="w-full">
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(6, 20, 27, 0.08)' }}>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider" style={{ color: '#4A5C6A', fontWeight: 400 }}>
                      NOME DA TAREFA
                    </th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider" style={{ color: '#4A5C6A', fontWeight: 400 }}>
                      TIPO
                    </th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider" style={{ color: '#4A5C6A', fontWeight: 400 }}>
                      SEGURANÇA
                    </th>
                    <th className="text-center py-3 px-4 text-xs uppercase tracking-wider" style={{ color: '#4A5C6A', fontWeight: 400, width: '80px' }}>
                      RESPONSÁVEL
                    </th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider" style={{ color: '#4A5C6A', fontWeight: 400 }}>
                      STATUS
                    </th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider w-12">
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.map((task: TaskDto) => {
                    const assigneeMember = members?.find((m: any) => m.id === task.assigneeMemberId);
                    const assignee = task.assignee || (assigneeMember?.user ? {
                      userId: assigneeMember.user.id,
                      userName: assigneeMember.user.name,
                      avatarUrl: assigneeMember.user.avatarUrl,
                      memberId: assigneeMember.id,
                    } : null);

                    const isCompletedOrCanceled =
                      task.status === 'DONE' || task.status === 'CANCELED';

                    return (
                      <tr
                        key={task.id}
                        className="cursor-pointer transition-colors"
                        style={{
                          borderBottom: '1px solid rgba(6, 20, 27, 0.08)',
                          backgroundColor: isCompletedOrCanceled
                            ? '#F3F4F6'
                            : 'transparent',
                        }}
                        onClick={() => {
                          if (!requirePermission('tasks.read', 'Ver tarefas')) return;
                          setSelectedTask(task);
                          setShowDetailModal(true);
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = isCompletedOrCanceled
                            ? '#E5E7EB'
                            : '#F9FAFB';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = isCompletedOrCanceled
                            ? '#F3F4F6'
                            : 'transparent';
                        }}
                      >
                        <td className="py-4 px-4">
                          <span style={{ fontSize: 12, color: '#2D3748' }}>
                            {truncateText(task.title, 50)}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <span
                            className="px-2.5 py-1 rounded-[10px]"
                            style={{
                              fontSize: 10,
                              fontWeight: 400,
                              backgroundColor: `${getTaskTypeColor(task.type)}20`,
                              color: getTaskTypeColor(task.type),
                            }}
                          >
                            {getTaskTypeLabel(task.type)}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <span style={{ fontSize: 12, color: '#2D3748' }}>
                            {task.type === 'EVENT' || task.type === 'HEARING'
                              ? (task.startAt ? formatDateTime(task.startAt) : '-')
                              : (task.securityDeadline ? formatDate(task.securityDeadline) : (task.dueDate ? formatDate(task.dueDate) : '-'))}
                          </span>
                        </td>
                        <td className="py-4 px-4" style={{ width: '80px' }}>
                          <div className="flex justify-center">
                            {assignee ? (
                              <TaskAssigneeAvatar assignee={assignee} />
                            ) : (
                              <span style={{ fontSize: 12, color: '#9BA8AB' }}>-</span>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <span
                            className="px-2.5 py-1 rounded-[10px]"
                            style={{
                              fontSize: 10,
                              fontWeight: 400,
                              backgroundColor: `${getTaskStatusColor(task.status)}20`,
                              color: getTaskStatusColor(task.status),
                            }}
                          >
                            {getTaskStatusLabel(task.status)}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <div
                            className="relative"
                            ref={(el) => { menuRef.current[task.id] = el; }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              type="button"
                              ref={(el) => { menuTriggerRefs.current[task.id] = el; }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setMenuOpenTaskId(menuOpenTaskId === task.id ? null : task.id);
                              }}
                              className="p-2 rounded-[10px] transition-opacity hover:opacity-80"
                              style={{ color: '#4A5C6A' }}
                              aria-label="Menu da tarefa"
                            >
                              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <circle cx="10" cy="5" r="1.5" fill="currentColor" />
                                <circle cx="10" cy="10" r="1.5" fill="currentColor" />
                                <circle cx="10" cy="15" r="1.5" fill="currentColor" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : !isLoading ? (
            <div className="text-center py-12">
              <p className="mb-1" style={{ fontSize: 16, fontWeight: 500, color: '#06141B' }}>Nenhuma tarefa encontrada</p>
              <p style={{ fontSize: 12, color: '#4A5C6A' }}>
                {debouncedSearch || statusFilter.length || typeFilter.length || assigneeFilter
                  ? 'Tente ajustar a busca ou os filtros.'
                  : 'Este processo ainda não possui tarefas cadastradas.'}
              </p>
            </div>
          ) : null}
          {totalListCount > LIST_PAGE_SIZE && !isLoading && (
            <div className="flex flex-col items-center gap-2 pt-6 border-t border-[rgba(6,20,27,0.08)] mt-4">
              <span className="text-[11px]" style={{ color: '#718096' }}>
                {(listPage - 1) * LIST_PAGE_SIZE + 1}–{Math.min(listPage * LIST_PAGE_SIZE, totalListCount)} de{' '}
                {totalListCount}
              </span>
              <div className="flex items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={() => setListPage((p) => Math.max(1, p - 1))}
                  disabled={listPage === 1}
                  className="flex items-center justify-center w-8 h-8 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: listPage === 1 ? '#F9FAFB' : '#06141B',
                    color: listPage === 1 ? '#9BA8AB' : '#F9FAFB',
                    borderRadius: 10,
                  }}
                  aria-label="Página anterior"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <span className="text-[12px] min-w-[7rem] text-center" style={{ color: '#2D3748' }}>
                  Página {listPage} de {totalListPages}
                </span>
                <button
                  type="button"
                  onClick={() => setListPage((p) => Math.min(totalListPages, p + 1))}
                  disabled={listPage === totalListPages}
                  className="flex items-center justify-center w-8 h-8 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: listPage === totalListPages ? '#F9FAFB' : '#06141B',
                    color: listPage === totalListPages ? '#9BA8AB' : '#F9FAFB',
                    borderRadius: 10,
                  }}
                  aria-label="Próxima página"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {openMenuTask &&
        taskMenuPosition &&
        createPortal(
          <div
            data-task-row-dropdown
            className="fixed w-48 py-1 rounded-[10px] z-[9999]"
            style={{
              top: taskMenuPosition.top,
              left: taskMenuPosition.left,
              backgroundColor: '#fff',
              boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(6, 20, 27, 0.08)',
            }}
          >
            <button
              type="button"
              onClick={() => handleEditTask(openMenuTask)}
              className="w-full text-left px-4 py-2 rounded-[8px] transition-colors hover:bg-[#F9FAFB]"
              style={{ fontSize: 12, fontWeight: 400, color: '#2D3748' }}
            >
              Editar tarefa
            </button>
            <button
              type="button"
              onClick={() => handleDeleteTask(openMenuTask.id)}
              className="w-full text-left px-4 py-2 rounded-[8px] transition-colors hover:bg-[#F9FAFB]"
              style={{ fontSize: 12, fontWeight: 400, color: '#DC2626' }}
            >
              Excluir tarefa
            </button>
          </div>,
          document.body,
        )}

      {showModal && (
        <TaskFormModal
          task={editingTask || undefined}
          processId={processId}
          onClose={() => {
            setShowModal(false);
            setEditingTask(null);
          }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['tasks', workspaceId, processId] });
            setShowModal(false);
            setEditingTask(null);
          }}
        />
      )}

      {showDetailModal && selectedTask && (
        <TaskDetailModal
          task={selectedTask}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedTask(null);
          }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['tasks', workspaceId, processId], exact: false });
          }}
        />
      )}

      <ArchivedProcessBlockModal
        isOpen={showArchivedBlockModal}
        onClose={() => setShowArchivedBlockModal(false)}
        workspaceId={workspaceId}
        clientId={clientId}
        processId={processId}
      />
    </div>
  );
}
